import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FolderLock, FolderPlus, Layers, RefreshCw, ScrollText, Search, Users } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { CreatorProfile } from '../components/CreatorProfile';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { logbookApiService, LogbookApiError } from '../services/logbookApi';
import { canProtectLogbook } from '../utils/logbookPermissions';
import { formatCount, relativeTime } from '../utils/logbook';
import { formatLocalDateTime } from '../utils/timestamps';
import { cn } from '../utils/cn';
import type { LogbookCase, LogbookCaseStatus } from '../types/logbook';

/**
 * Investigation cases.
 *
 * A case is Argus's own evidence file, not a Loglibrarian hold. Each holds a permanent copy of every
 * record attached to it, so it keeps working after central retention, a rollup or a world reset takes
 * the original away, and it can be attached to the note, warning or ban it justified.
 *
 * A case is opened from the evidence itself rather than here, because an empty case is not useful and
 * a moderator reaches this page to read cases rather than to invent them.
 */

const STATUSES: { id: LogbookCaseStatus | 'all'; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'archived', label: 'Archived' },
  { id: 'all', label: 'All' },
];

const STATUS_TONE: Record<LogbookCaseStatus, string> = {
  open: 'badge-success',
  closed: 'badge-neutral',
  archived: 'badge-neutral',
};

export function LogbookCasesView() {
  useDocumentTitle('Logbook cases');
  const { identity } = useAuth();
  const canManage = canProtectLogbook(identity);

  const [status, setStatus] = useState<LogbookCaseStatus | 'all'>('open');
  const [query, setQuery] = useState('');
  const [cases, setCases] = useState<LogbookCase[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LogbookApiError | Error | null>(null);
  const [nonce, setNonce] = useState(0);
  const debounced = useDebounce(query, 250);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    logbookApiService.listCases({
      status: status === 'all' ? undefined : status,
      q: debounced.trim() || undefined,
      limit: 200,
    }, controller.signal)
      .then((result) => {
        if (cancelled) return;
        setCases(result.cases);
        setTotal(result.total);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setCases([]);
        setError(caught instanceof Error ? caught : new Error('Cases could not be loaded.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [status, debounced, nonce]);

  const totals = useMemo(() => ({
    cases: total,
    records: cases.reduce((sum, entry) => sum + entry.record_count, 0),
    linked: cases.filter((entry) => entry.link_count > 0).length,
  }), [cases, total]);

  const notConfigured = error instanceof LogbookApiError && error.isNotConfigured;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Cases"
        description="Investigation files, each with its own permanent copy of the evidence."
        icon={FolderLock}
        backTo="/logbook"
        backLabel="Back to Logbook"
        actions={(
          <button type="button" className="glass-button" onClick={() => setNonce((value) => value + 1)} disabled={loading}>
            <RefreshCw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
            Refresh
          </button>
        )}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a case"
              className="form-input pl-9"
              aria-label="Find a case"
            />
          </div>
          <div className="segmented">
            {STATUSES.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="segmented-item segmented-item-sm"
                data-active={status === entry.id}
                onClick={() => setStatus(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-glass-400 tabular-nums shrink-0">{formatCount(cases.length)} shown</span>
        </div>
      </PageHeader>

      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Cases" value={formatCount(totals.cases)} icon={FolderLock} tone="amber" loading={loading} hint={status === 'all' ? 'Every status' : `${status} only`} />
        <StatTile label="Records held" value={formatCount(totals.records)} icon={Layers} tone="accent" loading={loading} />
        <StatTile label="Attached to a record" value={formatCount(totals.linked)} icon={Users} tone="purple" loading={loading} />
      </div>

      {notConfigured ? (
        <EmptyState icon={FolderLock} title="Logbook is not connected yet" description={error.message} />
      ) : error ? (
        <EmptyState
          tone="danger"
          icon={AlertTriangle}
          title="Cases could not be loaded"
          description={error.message}
          action={(
            <button type="button" className="glass-button" onClick={() => setNonce((value) => value + 1)}>
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
        />
      ) : loading && cases.length === 0 ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, index) => <SkeletonLoader key={index} className="h-24 w-full rounded-card" />)}
        </div>
      ) : cases.length === 0 ? (
        <EmptyState
          icon={FolderPlus}
          title={debounced.trim() ? 'No case matches that' : status === 'open' ? 'No case is open' : `No ${status} case`}
          description={debounced.trim()
            ? undefined
            : canManage
              ? 'Open one from a Logbook record: search, then add it to a new case.'
              : 'Cases appear here once a moderator opens one from a record.'}
          action={<Link to="/logbook" className="btn-primary"><ScrollText className="w-4 h-4" />Open Logbook</Link>}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {cases.map((entry) => <CaseSummaryCard key={entry.id} entry={entry} />)}
        </div>
      )}
    </div>
  );
}

/** One case as a card: what it is, how much it holds, and the window its evidence covers. */
const CaseSummaryCard = ({ entry }: { entry: LogbookCase }) => {
  const window = entry.earliest_event_at && entry.latest_event_at
    ? entry.earliest_event_at === entry.latest_event_at
      ? formatLocalDateTime(new Date(entry.latest_event_at))
      : `${formatLocalDateTime(new Date(entry.earliest_event_at))} to ${formatLocalDateTime(new Date(entry.latest_event_at))}`
    : null;

  return (
    <Link to={`/logbook/cases/${entry.id}`} className="glass-card glass-card-interactive p-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <span className="badge badge-accent font-mono">{entry.reference}</span>
        <span className={cn('badge', STATUS_TONE[entry.status])}>{entry.status}</span>
      </div>

      <h3 className="text-sm font-semibold text-white break-words">{entry.title}</h3>
      {entry.summary && <p className="text-xs text-glass-400 line-clamp-2">{entry.summary}</p>}

      <div className="mt-auto pt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-500 tabular-nums">
        <span className="inline-flex items-center gap-1">
          <Layers className="w-3.5 h-3.5" />
          {formatCount(entry.record_count)} {entry.record_count === 1 ? 'record' : 'records'}
        </span>
        {entry.subject_count > 0 && (
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {formatCount(entry.subject_count)}
          </span>
        )}
        {entry.link_count > 0 && (
          <span className="badge badge-info">{formatCount(entry.link_count)} linked</span>
        )}
      </div>

      {window && <div className="text-xs text-glass-500 tabular-nums">Evidence {window}</div>}

      <div className="flex flex-wrap items-center gap-1.5 text-xs text-glass-500">
        <span>opened by</span>
        <CreatorProfile creatorId={entry.created_by} inline />
        <span className="tabular-nums">{relativeTime(entry.created_at)}</span>
      </div>
    </Link>
  );
};
